import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { setLanguage, SupportedLanguage } from './config/languageConfig';
import { extractUTMParams, saveUTMParams, removeUTMFromParams, setupUTMDebugTools } from './utils/utmUtils';
import { savePendingStayId } from './utils/pendingStayUtils';
import { savePromotionUserId } from './utils/promotionUserIdUtils';

/**
 * Figma MCP html-to-design: URL 해시가 `#figmacapture=…`만 있으면 HashRouter가
 * 유효 경로로 인식하지 못해 NotFound가 뜬다. React 부팅 전에 `#/home?…`로 바꿔
 * 홈 라우트가 매칭되게 한다(일반 사용자는 이 해시를 쓰지 않음).
 */
function normalizeFigmaCaptureHashBeforeBoot(): void {
  const h = window.location.hash;
  if (!/^#figmacapture=/.test(h)) return;
  const qs = h.slice(1);
  const { pathname, search } = window.location;
  window.history.replaceState(null, '', `${pathname}${search}#/home?${qs}`);
}
normalizeFigmaCaptureHashBeforeBoot();

/**
 * Figma MCP — 주문·결제 캡처 전용 (localhost만).
 * 해시 쿼리에 `figmacapture` + `figmaorderfixture=1`이 있으면 온보딩 생략·주소·담긴 카트(본죽 ZRpbK5Tc / 메뉴 Jj7PsuV1)를 주입한다.
 * 로컬 백엔드는 특정 더미 addressId로도 calculate-order·POST /api/carts가 동작하는 것을 전제로 한다(팀 DB가 다르면 REACT_APP_FIGMA_ORDERFLOW_ADDRESS_ID로 덮어쓴다).
 */
function applyFigmaOrderFlowFixtureBeforeBoot(): void {
  const host = window.location.hostname;
  if (host !== "localhost" && host !== "127.0.0.1") return;

  const h = window.location.hash;
  const qIdx = h.indexOf("?");
  if (qIdx === -1) return;
  const params = new URLSearchParams(h.slice(qIdx + 1));
  if (!params.get("figmacapture") || params.get("figmaorderfixture") !== "1") {
    return;
  }

  const addressId =
    process.env.REACT_APP_FIGMA_ORDERFLOW_ADDRESS_ID ||
    "00000000-0000-0000-0000-000000000001";

  try {
    localStorage.setItem("hasSeenOnboarding", "true");
    localStorage.setItem(
      "userAddress",
      JSON.stringify({
        mainAddress: "서울특별시 강남구 신사동 (Figma order fixture)",
        detailAddress: " ",
        isComplete: true,
        lat: 37.51784761,
        lng: 127.0222763,
        addressId,
      }),
    );
    localStorage.setItem("cartVersion", "2");
    localStorage.setItem(
      "cart",
      JSON.stringify({
        id: "figma-orderflow-fixture",
        restaurantId: "ZRpbK5Tc",
        restaurantName: "Bonjuk & Bibimbap Café-Gentleman's shop",
        restaurantNameEn: "Bonjuk & Bibimbap Café-Gentleman's shop",
        items: [
          {
            id: "Jj7PsuV1",
            name: "Beef & Seaweed Porridge",
            nameEn: "Beef & Seaweed Porridge",
            price: 12000,
            quantity: 2,
            options: [],
            priceKrw: 12000,
            priceUsd: 9.85,
            priceJpy: 1577,
            priceCny: 68,
            priceTwd: 315,
          },
        ],
      }),
    );
  } catch (e) {
    console.warn("[Figma order fixture] apply failed:", e);
  }
}
applyFigmaOrderFlowFixtureBeforeBoot();

// Wrapper component to handle redirect from 404.html and non-hash URLs
const AppWithRedirectHandler: React.FC = () => {
  // UTM 디버깅 도구 초기화
  useEffect(() => {
    setupUTMDebugTools();
  }, []);

  // After the app mounts, check if there's a redirect path stored in sessionStorage
  useEffect(() => {
    const redirectPath = sessionStorage.getItem('redirectPath');
    
    if (redirectPath) {
      // Clear the redirect path from sessionStorage
      sessionStorage.removeItem('redirectPath');
      
      // Get the base path for the GitHub Pages site
      const basePath = '/home';
      
      // If we have a redirect path, navigate to that route inside the app
      // This needs to be done after a slight delay to allow the router to initialize
      setTimeout(() => {
        window.history.pushState({}, '', basePath + redirectPath);
        // Dispatch a popstate event so the router can detect the change
        window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
      }, 100);
    }
  }, []);

  // Handle non-hash URLs by redirecting to hash URLs
  useEffect(() => {
    const currentPath = window.location.pathname;
    const currentHash = window.location.hash;
    const currentSearch = window.location.search;
    
    // URL 쿼리 파라미터에서 stayId, promotionUserId, lang, UTM 확인
    const urlParams = new URLSearchParams(currentSearch);
    const stayIdFromQuery = urlParams.get('stayId');
    const promotionUserIdFromQuery = urlParams.get('promotionUserId');
    const langFromQuery = urlParams.get('lang');
    
    // UTM 파라미터 처리
    const utmParams = extractUTMParams(urlParams);
    const hasUTMParams = Object.keys(utmParams).length > 0;
    
    let hasParamsToStore = false;
    
    if (stayIdFromQuery) {
      // localStorage에 stay ID 저장
      const saved = savePendingStayId(stayIdFromQuery);
      if (saved) {
        // 저장 성공 시에만 URL에서 stayId 제거 (실패 시 URL에 유지하여 GlobalUrlParameterHandler에서 재처리)
        urlParams.delete('stayId');
        hasParamsToStore = true;
      }
    }
    
    if (promotionUserIdFromQuery) {
      // localStorage에 promotion user ID 저장
      savePromotionUserId(promotionUserIdFromQuery, 'deeplink');
      urlParams.delete('promotionUserId');
      hasParamsToStore = true;
    }
    
    if (langFromQuery) {
      // 언어 설정 변경
      const supportedLanguages = ['en', 'ko', 'ja', 'zh-CN', 'zh-TW'];
      if (supportedLanguages.includes(langFromQuery)) {
        setLanguage(langFromQuery as SupportedLanguage);
      }
      urlParams.delete('lang');
      hasParamsToStore = true;
    }
    
    if (hasUTMParams) {
      // sessionStorage에 UTM 파라미터 저장 (최신 UTM으로 덮어쓰기)
      saveUTMParams(utmParams);
      // URL에서 UTM 파라미터 제거
      removeUTMFromParams(urlParams);
      hasParamsToStore = true;
    }
    
    // Special handling for stay URLs first
    // URL 끝의 #, /, #/ 등을 제거하여 깔끔하게 처리
    const cleanPath = currentPath.replace(/[/#]+$/, '');
    
    // 온보딩 관련 해시인지 확인하는 함수
    const isOnboardingHash = (hash: string): boolean => {
      return hash === '' || 
             hash === '/' || 
             hash === '#' ||
             hash === '#/' ||
             hash.startsWith('#/onboarding') ||
             hash.startsWith('/onboarding');
    };
    
    const stayMatch = cleanPath.match(/\/home\/stays\/([^/#?]+)$/);
    if (stayMatch) {
      const stayId = stayMatch[1];
      
      // localStorage에 stay ID 저장 (온보딩 상태와 관계없이)
      const saved = savePendingStayId(stayId);
      
      // 온보딩이 필요한지 확인
      const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
      // localStorage 실패 시 stayId를 URL param으로 전달하여 GlobalUrlParameterHandler에서 재처리
      const stayIdParam = saved ? '' : `?stayId=${encodeURIComponent(stayId)}`;
      
      if (!hasSeenOnboarding) {
        // 온보딩이 필요한 경우 온보딩 해시로 리다이렉트
        window.location.href = `${window.location.origin}/home/${stayIdParam}#/onboarding`;
        return;
      } else {
        // 온보딩이 완료된 경우 깔끔한 홈 URL로 리다이렉트
        const targetHash = (!currentHash || isOnboardingHash(currentHash)) ? '#' : currentHash;
        window.location.href = `${window.location.origin}/home/${stayIdParam}${targetHash}`;
        return;
      }
    }

    // Special handling for new domain stay URLs
    // URL 끝에 #, /, #/ 등이 붙는 경우를 먼저 제거하고 처리
    const newDomainStayMatch = cleanPath.match(/^\/stays\/([^/#?]+)$/);
    if (newDomainStayMatch) {
      const stayId = newDomainStayMatch[1];
      
      // localStorage에 stay ID 저장 (온보딩 상태와 관계없이)
      const saved = savePendingStayId(stayId);
      
      // 저장 확인 (모바일 디버깅용)
      console.log('Stay ID 저장 확인:', { stayId, saved, cleanPath });
      
      // 온보딩이 필요한지 확인
      const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
      // localStorage 실패 시 stayId를 URL param으로 전달하여 GlobalUrlParameterHandler에서 재처리
      const stayIdParam = saved ? '' : `?stayId=${encodeURIComponent(stayId)}`;
      
      if (!hasSeenOnboarding) {
        // 온보딩이 필요한 경우 온보딩으로 리다이렉트
        window.location.href = `${window.location.origin}/home/${stayIdParam}#/onboarding`;
        return;
      } else {
        // 온보딩이 완료된 경우 홈으로 리다이렉트
        window.location.href = `${window.location.origin}/home/${stayIdParam}#`;
        return;
      }
    }
    
    // If we're not on the base path and there's no hash, redirect to hash URL
    if (currentPath !== '/' && currentPath !== '/home' && !currentHash) {
      // Extract the path after /home if it exists
      let routePath = currentPath;
      if (currentPath.startsWith('/home')) {
        routePath = currentPath.substring(5); // Remove '/home' prefix
      }
      
      // 파라미터들을 제거한 깔끔한 URL로 리다이렉트
      const cleanSearch = urlParams.toString();
      const cleanUrl = cleanSearch ? `?${cleanSearch}` : '';
      
      // Redirect to hash URL
      window.location.href = `${window.location.origin}${window.location.pathname.includes('/home') ? '/home' : ''}${cleanUrl}/#${routePath}`;
      return;
    }
    
    // 파라미터만 있는 경우 (경로는 이미 올바른 경우)
    if (hasParamsToStore) {
      // 파라미터들을 제거한 깔끔한 URL로 리다이렉트
      const cleanSearch = urlParams.toString();
      const cleanUrl = cleanSearch ? `?${cleanSearch}` : '';
      
      if (currentHash) {
        // 해시 URL이 있는 경우
        window.location.href = `${window.location.origin}${currentPath}${cleanUrl}${currentHash}`;
      } else {
        // 해시 URL이 없는 경우 해시 URL로 리다이렉트
        window.location.href = `${window.location.origin}${window.location.pathname.includes('/home') ? '/home' : ''}${cleanUrl}/#`;
      }
      return;
    }
  }, []);

  return <App />;
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <AppWithRedirectHandler />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
